import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Search } from 'lucide-react';

export default function SelectField({ placeholder, value, onChange, options, delay = 0, searchable = false }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter options based on search term
  useEffect(() => {
    if (searchable && searchTerm) {
      const filtered = options.filter(option =>
        option.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      // If no results found, show "Other" option if it exists in the original options
      if (filtered.length === 0 && options.includes('Other')) {
        setFilteredOptions(['Other']);
      } else {
        setFilteredOptions(filtered);
      }
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options, searchable]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchable && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen, searchable]);

  const handleOptionSelect = (option) => {
    onChange(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const displayOptions = searchable ? filteredOptions : options;
  const maxHeight = options.length > 8 ? 'max-h-64' : 'max-h-auto';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      className="relative group"
      ref={dropdownRef}
    >
      <div className="rounded-xl border border-gray-200 transition-all duration-300 bg-white/95 group-hover:border-gray-300 group-focus-within:border-emerald-600 group-focus-within:ring-2 group-focus-within:ring-emerald-100">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="w-full px-4 py-3 text-sm font-medium bg-transparent rounded-[0.95rem] outline-none transition-all duration-300 text-left flex items-center justify-between"
        >
          <span className={value ? 'text-gray-900' : 'font-normal text-gray-400'}>
            {value || placeholder}
          </span>
          <ChevronDown size={16} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, y: 6, scale: 0.98 }}
              animate={{ opacity: 1, y: -8, scale: 1 }}
              exit={{ opacity: 0, y: 6, scale: 0.98 }}
              transition={{ duration: 0.18 }}
              className="absolute bottom-full z-50 mb-2 w-full bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden"
            >
              {searchable && (
                <div className="p-2 border-b border-gray-100">
                  <div className="relative">
                    <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder={`Search ${placeholder.toLowerCase()}...`}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-600"
                    />
                  </div>
                </div>
              )}
              <div className={`py-1 ${maxHeight} overflow-y-auto`}>
                {displayOptions.length > 0 ? (
                  displayOptions.map((option) => (
                    <button
                      key={option}
                      onClick={() => handleOptionSelect(option)}
                      className="px-4 py-2 w-full text-sm text-left transition-colors hover:bg-emerald-50 focus:bg-emerald-50 focus:outline-none"
                    >
                      {option}
                    </button>
                  ))
                ) : (
                  <div className="px-4 py-2 text-sm text-gray-500 text-center">
                    {options.includes('Other') ? 'No matches found. Select "Other" to enter a custom option.' : 'No options found'}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}


